import React, { useEffect, useState } from "react";
import { parseOpeningHoursToUI, parseUIToOpeningHours } from "../../utils/utilOpeningHours";
import AddNewItemButton from "../buttons/AddNewItemButton";
import TimeWindow from "./TimeWindow";

function convertToAmPm(time) {
  if (!time) {
    return "";
  }
  const [hour, minute] = time.split(":").map(Number);
  const amPm = hour >= 12 && hour <= 23 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${minute.toString().padStart(2, "0")} ${amPm}`;
}

export const generateTimeWindowsText = (timeWindows, prefixText = "", firstDay = "Sunday") => {
  if (!timeWindows || timeWindows.length === 0) {
    return "";
  }

  if (timeWindows.some((timeWindow) => timeWindow.open === "" || timeWindow.close === "")) {
    return "";
  }

  // Step 1: Define the days order and sort opening hours accordingly
  const daysOrder = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  timeWindows.sort((a, b) => daysOrder.indexOf(a.day) - daysOrder.indexOf(b.day));

  // Step 2: Group opening hours by day and by time slots
  const groupedHours = timeWindows.reduce((acc, { day, open, close }) => {
    const key = `${open}-${close}`;
    if (!acc[day]) acc[day] = {};
    if (!acc[day][key]) acc[day][key] = { open, close };
    return acc;
  }, {});

  // Step 3: Flatten to an array with each day having a list of time slots
  const dayTimeSlots = daysOrder
    .map((day) => {
      const timeSlots = groupedHours[day] ? Object.values(groupedHours[day]) : [];
      return { day, timeSlots };
    })
    .filter(({ timeSlots }) => timeSlots.length > 0);

  // Step 4: Merge consecutive days with identical time slots
  const mergedSchedule = [];
  let tempDays = [];
  let lastTimeSlots = null;

  dayTimeSlots.forEach(({ day, timeSlots }, index) => {
    if (JSON.stringify(timeSlots) === JSON.stringify(lastTimeSlots)) {
      tempDays.push(day);
    } else {
      if (tempDays.length > 0) {
        mergedSchedule.push({
          days: daysToString(tempDays),
          timeSlots: lastTimeSlots,
        });
      }
      tempDays = [day];
    }
    lastTimeSlots = timeSlots;

    // For the last element, push remaining days
    if (index === dayTimeSlots.length - 1) {
      mergedSchedule.push({
        days: daysToString(tempDays),
        timeSlots,
      });
    }
  });

  // Step 5: Format the output with "and" before the last option in any list
  const formattedOutput = mergedSchedule.map(({ days, timeSlots }) => {
    const times = timeSlots.map(({ open, close }) => `${convertToAmPm(open)} to ${convertToAmPm(close)}`);

    // Add "and" before the last time slot if there are multiple
    const timesFormatted = times.length > 1 ? times.slice(0, -1).join(", ") + " and " + times.slice(-1) : times[0];

    return `${days}: ${timesFormatted}`;
  });

  return `${prefixText} ${formattedOutput.join("; ")}.`;
};

const daysToString = (days) => {
  const daysOrder = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  if (days.length === 0) {
    return "";
  }

  if (days.length === 1) {
    return days[0];
  }

  if (days.length === 2) {
    return `${days[0]} and ${days[1]}`;
  }

  return `${days[0]} to ${days[days.length - 1]}`;
};

const TimeWindows = ({
  timeWindows,
  setTimeWindows,
  minRows = 1,
  maxRows = 7,
  minHoursOptionsRows = 1,
  maxHoursOptionsRows = 3,
  availableDays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
  firstDay = "Sunday",
  interval = 30,
}) => {
  const [rows, setRows] = useState([]);

  const onAddNewRow = () => {
    setRows([...rows, { selectedDays: [], timeSlots: [{ from: "", to: "" }] }]);
  };

  const onDeleteRow = (index) => {
    setRows(rows.filter((row, i) => i !== index));
  };

  const isValidTimeWindows = () => {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (row.selectedDays.length === 0) {
        return false;
      }
      for (let j = 0; j < row.timeSlots.length; j++) {
        const timeSlot = row.timeSlots[j];
        if (!timeSlot.from || !timeSlot.to) {
          return false;
        }
      }
    }
    return true;
  };

  useEffect(() => {
    setTimeWindows([...parseUIToOpeningHours(rows)]);
  }, [rows]);

  useEffect(() => {
    if (timeWindows.length === 0 && minRows > 0) {
      const newRows = [...rows];
      while (newRows.length < minRows) {
        newRows.push({ selectedDays: [], timeSlots: [{ from: "", to: "" }] });
      }
      setRows(newRows);
      return;
    }

    const parsedTimeWindows = parseOpeningHoursToUI(timeWindows);
    setRows(parsedTimeWindows);
  }, []);

  return (
    <div className="w-full flex flex-col gap-4">
      {rows.map((row, index) => (
        <div key={index} className="flex flex-col">
          <TimeWindow
            interval={interval}
            onDeleteRow={() => onDeleteRow(index)}
            availableDays={availableDays}
            firstDay={firstDay}
            selectedDays={row.selectedDays}
            setSelectedDays={(selectedDays) => {
              const newRows = [...rows];
              newRows[index].selectedDays = selectedDays;
              setRows(newRows);
            }}
            timeSlots={row.timeSlots}
            setTimeSlots={(timeSlots) => {
              const newRows = [...rows];
              newRows[index].timeSlots = timeSlots;
              setRows(newRows);
            }}
            minHoursOptionsRows={minHoursOptionsRows}
            maxHoursOptionsRows={maxHoursOptionsRows}
          />
          {index < rows.length - 1 && <div className="h-[1px] w-full bg-slate-200"></div>}
        </div>
      ))}
      <div className="flex flex-col md:flex-row justify-start items-start md:items-center">
        <AddNewItemButton
          title="+ Add time slot"
          onClick={onAddNewRow}
          disabled={rows.length >= maxRows || !isValidTimeWindows()}
          onDisabledTooltip={!isValidTimeWindows() ? "Finish filling out incomplete items before adding a new one" : ""}
        />
        {rows.length >= maxRows && (
          <span className="text-[10px] md:text-[12px]">you've reached the maximum number of time slots available.</span>
        )}
      </div>
    </div>
  );
};

export default TimeWindows;
